import { useState, useEffect } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { sourcesApi, type EntityGroupWithFacets } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Check, X, ChevronDown, ChevronRight, Lightbulb } from 'lucide-react'

interface EntityReviewModalProps {
  sourceId: string
  onClose: () => void
  onComplete: () => void
}

export function EntityReviewModal({ sourceId, onClose, onComplete }: EntityReviewModalProps) {
  const { accountId, projectId } = useSessionStore()
  const [entityGroups, setEntityGroups] = useState<EntityGroupWithFacets[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [processingGroups, setProcessingGroups] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadEntityGroups()
  }, [sourceId, accountId, projectId])

  const loadEntityGroups = async () => {
    if (!accountId || !projectId) return

    try {
      setLoading(true)
      setError(null)
      const groups = await sourcesApi.getEntityGroups(accountId, projectId, sourceId)
      setEntityGroups(groups)
      // Auto-expand first group
      if (groups.length > 0) {
        setExpandedGroups(new Set([groups[0].id]))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load entity groups')
    } finally {
      setLoading(false)
    }
  }

  const toggleExpanded = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  const handleApprove = async (groupId: string) => {
    if (!accountId || !projectId) return

    try {
      setProcessingGroups(prev => new Set(prev).add(groupId))
      await sourcesApi.approveEntityGroup(accountId, projectId, sourceId, groupId)
      setEntityGroups(prev => prev.filter(g => g.id !== groupId))

      // If all groups processed, complete
      if (entityGroups.length === 1) {
        onComplete()
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to approve entity')
    } finally {
      setProcessingGroups(prev => {
        const next = new Set(prev)
        next.delete(groupId)
        return next
      })
    }
  }

  const handleReject = async (groupId: string) => {
    if (!accountId || !projectId) return

    try {
      setProcessingGroups(prev => new Set(prev).add(groupId))
      await sourcesApi.rejectEntityGroup(accountId, projectId, sourceId, groupId)
      setEntityGroups(prev => prev.filter(g => g.id !== groupId))

      // If all groups processed, complete
      if (entityGroups.length === 1) {
        onComplete()
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reject entity')
    } finally {
      setProcessingGroups(prev => {
        const next = new Set(prev)
        next.delete(groupId)
        return next
      })
    }
  }

  const handleApproveAll = async () => {
    if (!accountId || !projectId) return
    if (!confirm(`Approve all ${entityGroups.length} entities?`)) return

    try {
      setLoading(true)
      await Promise.all(
        entityGroups.map(group =>
          sourcesApi.approveEntityGroup(accountId, projectId, sourceId, group.id)
        )
      )
      onComplete()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to approve all entities')
      loadEntityGroups()
    } finally {
      setLoading(false)
    }
  }

  const handleRejectAll = async () => {
    if (!accountId || !projectId) return
    if (!confirm(`Reject all ${entityGroups.length} entities?`)) return

    try {
      setLoading(true)
      await Promise.all(
        entityGroups.map(group =>
          sourcesApi.rejectEntityGroup(accountId, projectId, sourceId, group.id)
        )
      )
      onComplete()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reject all entities')
      loadEntityGroups()
    } finally {
      setLoading(false)
    }
  }

  if (loading && entityGroups.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col">
          <CardHeader>
            <CardTitle>Review Detected Entities</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-auto">
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-200 border-t-zinc-800" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-4xl">
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button onClick={loadEntityGroups}>Retry</Button>
              <Button variant="outline" onClick={onClose}>Close</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (entityGroups.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-4xl">
          <CardHeader>
            <CardTitle>No Entities Detected</CardTitle>
            <CardDescription>
              No entity groups were detected in this document.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={onComplete}>Close</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-5xl max-h-[90vh] flex flex-col">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Review Detected Entities</CardTitle>
              <CardDescription>
                {entityGroups.length} {entityGroups.length === 1 ? 'entity' : 'entities'} detected. Review and approve or reject each one.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectAll}
                disabled={loading}
              >
                Reject All
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleApproveAll}
                disabled={loading}
              >
                Approve All
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-auto p-6 space-y-4">
          {entityGroups.map((group) => {
            const isExpanded = expandedGroups.has(group.id)
            const isProcessing = processingGroups.has(group.id)

            return (
              <Card key={group.id} className="border-2">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <button
                          onClick={() => toggleExpanded(group.id)}
                          className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                        <CardTitle className="text-lg">{group.entityLabel}</CardTitle>
                        <Badge variant="outline">{group.entityType}</Badge>
                        {group.aiSuggested && (
                          <Badge variant="secondary" className="gap-1">
                            <Lightbulb className="h-3 w-3" />
                            AI Suggested
                          </Badge>
                        )}
                      </div>
                      {group.entityDescription && (
                        <CardDescription className="ml-9">
                          {group.entityDescription}
                        </CardDescription>
                      )}
                      {group.aiReason && (
                        <div className="ml-9 mt-2 text-sm text-zinc-600 dark:text-zinc-400 italic">
                          {group.aiReason}
                        </div>
                      )}
                      {group.aiConfidence && (
                        <div className="ml-9 mt-1">
                          <Badge variant="outline" className="text-xs">
                            Confidence: {Math.round(group.aiConfidence * 100)}%
                          </Badge>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(group.id)}
                        disabled={isProcessing}
                        className="gap-1"
                      >
                        <X className="h-4 w-4" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleApprove(group.id)}
                        disabled={isProcessing}
                        className="gap-1"
                      >
                        <Check className="h-4 w-4" />
                        Approve
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && group.facets.length > 0 && (
                  <CardContent className="pt-0 space-y-3">
                    <div className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                      Evidence ({group.facets.length} {group.facets.length === 1 ? 'facet' : 'facets'}):
                    </div>
                    {group.facets.map((facet) => (
                      <div
                        key={facet.id}
                        className="bg-zinc-50 dark:bg-zinc-900 rounded-lg p-3 space-y-2"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="secondary" className="text-xs">
                                {facet.role}
                              </Badge>
                              <span className="text-xs text-zinc-500">
                                {facet.facetTypeId}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {Math.round(facet.confidence * 100)}%
                              </Badge>
                            </div>
                            <div className="text-sm font-medium mb-1">
                              {facet.content}
                            </div>
                            {facet.evidence && (
                              <div className="text-xs text-zinc-600 dark:text-zinc-400 italic border-l-2 border-zinc-300 dark:border-zinc-700 pl-2">
                                "{facet.evidence}"
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            )
          })}
        </CardContent>
      </Card>
    </div>
  )
}
